import React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { GetEventDetailedQuery, EventStatusEnum } from '@graphql';
import { useSession } from '@hooks';
import LikeButton from './buttons/LikeButton';
import { ParticipationButton } from './buttons/ParticipationButton';
import { DialogEditContext, WriteButton } from '../../common';

interface IDCardProps {
  event: GetEventDetailedQuery['event'];
  scrollToTickets: () => void;
}

function IDCard(props: IDCardProps) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('IDCard must be used inside DialogEditContext');
  }

  const { isOwner } = context;
  const { event, scrollToTickets } = props;
  const { status, title, category, likers, tickets, isPrivate, participants } = event;
  const { account } = useSession();
  const theme = useTheme();
  const { t } = useTranslation('cta');
  const avatarScale = 75;

  return (
    <Stack spacing={3} width="100%">
      <Stack spacing={3} pt={2} justifyContent="space-between" alignItems="center">
        <Stack direction="row" flex={1} width="100%" justifyContent="space-between" alignItems="center">
          <Avatar
            src={event.thumb}
            alt={`event-avatar-${title}`}
            sx={{ width: avatarScale, height: avatarScale, bgcolor: theme.palette.background.default }}
          >
            <theme.icons.event
              sx={{ width: avatarScale, height: avatarScale, color: theme.palette.text.primary }}
            />
          </Avatar>
          <Stack flex={1} justifyContent="center" alignItems="center">
            <Typography variant="body1" color="textPrimary" fontWeight="bold">
              {getCount(likers.count)}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {likers.count > 1 ? 'likes' : 'like'}
            </Typography>
          </Stack>
          {isPrivate ? (
            <>
              <Stack flex={1} justifyContent="center" alignItems="center">
                <Typography variant="body1" color="textPrimary" fontWeight="bold">
                  {participants.invited}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {participants.invited > 1 ? 'invités' : 'invité'}
                </Typography>
              </Stack>
              <Stack flex={1} justifyContent="center" alignItems="center">
                <Typography variant="body1" color="textPrimary" fontWeight="bold">
                  {participants.confirmed}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {participants.confirmed > 1 ? 'participants' : 'participant'}
                </Typography>
              </Stack>
            </>
          ) : (
            <Stack flex={1} justifyContent="center" alignItems="center">
              <Typography variant="body1" color="textPrimary" fontWeight="bold">
                {getCount(likers.count)}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {likers.count > 1 ? 'billets vendus' : 'billet vendu'}
              </Typography>
            </Stack>
          )}
        </Stack>
        {Boolean(!isOwner || tickets.length) && (
          <Stack spacing={1} flex={1} width="100%">
            <StatusTicketsSlot status={status} tickets={tickets} scrollToTickets={scrollToTickets} />
            {!isOwner && (
              <Stack spacing={1} direction="row" width="100%">
                <LikeButton
                  eventId={event.id}
                  isLiking={event.isLiking}
                  disabled={!account || event.status === EventStatusEnum.Draft}
                />
                <WriteButton
                  recipientId={event.creator.id}
                  disabled={!account || event.status === EventStatusEnum.Draft}
                />
              </Stack>
            )}
            {event.creator.id !== account?.id && (
              <ParticipationButton eventId={event.id} status={participants.me?.status} />
            )}
          </Stack>
        )}
      </Stack>
      <Stack flex={1}>
        <Typography variant="h1" color="textPrimary" fontWeight="bold" sx={{ fontSize: '2rem !important' }}>
          {title}
        </Typography>

        <Stack direction="row" width="100%" alignItems="center">
          {category?.name && (
            <Typography variant="body1" color="textSecondary" fontWeight="bold">
              {t(`category.${category?.name}`, { ns: 'event' })}
            </Typography>
          )}

          <Box sx={{ ml: 'auto', display: 'flex' }}>
            <Chip
              size="small"
              label={t(`privacy.${isPrivate ? 'private' : 'public'}`, { ns: 'event' })}
              variant="outlined"
              color="uncolored"
            />
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface StatusTicketsSlotProps {
  tickets: GetEventDetailedQuery['event']['tickets'];
  status: GetEventDetailedQuery['event']['status'];
  scrollToTickets: () => void;
}

function StatusTicketsSlot(props: StatusTicketsSlotProps) {
  const { status, tickets, scrollToTickets } = props;
  const theme = useTheme();
  const { t } = useTranslation('cta');

  const isTicketFree = tickets.length === 1 && tickets[0].price === 0;

  // not published events yet
  if (
    [
      EventStatusEnum.Draft,
      EventStatusEnum.Postponed,
      EventStatusEnum.Canceled,
      EventStatusEnum.Deleted,
      EventStatusEnum.Removed,
    ].includes(status)
  ) {
    const colorMap: Partial<Record<EventStatusEnum, string>> = {
      [EventStatusEnum.Draft]: theme.palette.grey[500],
      [EventStatusEnum.Postponed]: theme.palette.warning.main,
      [EventStatusEnum.Canceled]: theme.palette.error.main,
      [EventStatusEnum.Deleted]: theme.palette.error.main,
      [EventStatusEnum.Removed]: theme.palette.error.main,
    };

    const statusTextMap: Partial<Record<EventStatusEnum, string>> = {
      [EventStatusEnum.Draft]: t(`status.${EventStatusEnum.Draft}`, { ns: 'event' }),
      [EventStatusEnum.Postponed]: t(`status.${EventStatusEnum.Postponed}`, { ns: 'event' }),
      [EventStatusEnum.Canceled]: t(`status.${EventStatusEnum.Canceled}`, { ns: 'event' }),
      [EventStatusEnum.Deleted]: t(`status.${EventStatusEnum.Deleted}`, { ns: 'event' }),
      [EventStatusEnum.Removed]: t(`status.${EventStatusEnum.Removed}`, { ns: 'event' }),
    };

    return (
      <Stack
        p={1}
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: alpha(colorMap[status] ?? theme.palette.background.default, 0.3),
          border: `1px solid ${colorMap[status] ?? theme.palette.background.default}`,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="body1"
          fontWeight="bold"
          textAlign="center"
          sx={{ color: colorMap[status] ?? theme.palette.text.primary }}
        >
          {statusTextMap[status]}
        </Typography>
      </Stack>
    );
  }

  // published events
  if (isTicketFree) {
    return (
      <Stack
        p={1}
        alignItems="center"
        justifyContent="center"
        sx={{ backgroundColor: alpha(theme.palette.success.dark, 0.4), borderRadius: 1 }}
      >
        <Typography variant="body1" color="textSecondary" fontWeight="bold" textAlign="center">
          {t('free')}
        </Typography>
      </Stack>
    );
  }

  if (tickets.length > 1) {
    return (
      <Button variant="contained" color="primary" fullWidth onClick={scrollToTickets}>
        {t('tickets')}
      </Button>
    );
  }

  return null;
}

function getCount(number: number) {
  if (number < 1000) {
    return number;
  } else if (number < 1_000_000) {
    return `${Math.floor(number / 1000)}K`;
  } else if (number < 1_000_000_000) {
    return `${Math.floor(number / 1_000_000)}M`;
  } else if (number < 1_000_000_000_000) {
    return `${Math.floor(number / 1_000_000_000)}B`;
  }
}

export default IDCard;
